import { Fragment, useEffect, useState } from "react";
import { deleteApiKeys, getApiKeys } from "../../../api/apiKeysApis";
import { ICommonCloudApiResponse } from "../../../interfaces";
import Logger from "../../../libs/Logger";
import { ErrorReporter } from "../../../libs/ErrorReporter";
import { EventTracker } from "../../../libs/EventTracker";
import ApiKeyItems from "./ApiKeyItem";
import { customToast } from "../../../utils/customToast";
import TerminalIcon from "../../../assets/svg/TerminalIcon";
import KeyIcon from "../../../assets/svg/KeyIcon";
import RightPaginationIcon from "../../../assets/svg/RightPaginationIcon";
import { showErrorToast } from "../../../utils/errorsToast";
import { observer } from "mobx-react";
import { useStore } from "../../../storeManager/stores";
import Placeholder from "react-select/dist/declarations/src/components/Placeholder";
import PulseLoader from "@mecha/design-components/dist/src/components/Elements/PulseLoader";
import LinkButton from "@mecha/design-components/dist/src/components/Elements/LinkButton";
import { EventTrackingCodes } from "../../../interfaces/eventsInterfaces";

interface ApiKeyListProps {
  setLimitExceeded: any;
}

const ApiKeyList = () => {
  const log = new Logger({ module: "Settings", component: "ApiKeyList" });
  const { fetchApiKeys, apiKeyList, fetchingKeys } = useStore().settingsStore;
  const keysExist = apiKeyList.length > 0;
  const [loading, setLoading] = useState(false);

  const ApiKeyUses = [
    {
      // eslint-disable-next-line react/jsx-no-undef
      icon: <TerminalIcon className="h-[38px] w-[38px]" />,
      title: "Setup Developer CLI ",
      description: "How to install a CLI- 101",
      link: "#",
    },
    {
      icon: <KeyIcon className="h-[38px] w-[38px]" />,
      title: "Use our REST API ",
      description: "Understand how to use the REST API ",
      link: "#",
    },
  ];
  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleDelete = async id => {
    //deleting api key
    setLoading(true);
    const deleteResponse = (await deleteApiKeys(id).catch(err => {
      log.error(
        { error: err },
        `(handleDelete) - (handleDelete) - error in deleting api key {id: ${id}}`,
      );
      ErrorReporter.collect(err, `Error in deleting API Key {id: ${id}}`);
      showErrorToast(err);
      setLoading(false);
    })) as ICommonCloudApiResponse;
    if (!deleteResponse?.success) {
      setLoading(false);
      return;
    }
    EventTracker.track(EventTrackingCodes.APIKEY_DELETED, { keyId: id });
    //fetching api keys after deletion
    await fetchApiKeys();
    customToast({
      msg: "API key successfully deleted",
      toastType: "SUCCESS",
    });
    setLoading(false);
    EventTracker.track(`Deleted API Key {id: ${id}}`);
    log.info({}, `(handleDelete) - API Key deleted successfully {id: ${id}}`);
  };

  if (fetchingKeys || loading) {
    return (
      <div className="w-full flex flex-col mt-5 gap-10">
        <div className="flex flex-col gap-3">
          <PulseLoader className="w-full rounded-md" />
          <PulseLoader className="w-72 rounded-md" />
          <PulseLoader className="w-52 rounded-md" />
        </div>
        <div className="flex flex-col gap-3">
          <PulseLoader className="w-full rounded-md" />
          <PulseLoader className="w-72 rounded-md" />
          <PulseLoader className="w-52 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {apiKeyList?.map((item, index) => (
        <Fragment key={index}>
          <span className="px-2 py-0.5">
            <ApiKeyItems
              item={item}
              handleDelete={handleDelete}
              index={index}
            />
          </span>
        </Fragment>
      ))}
      <div>
        <p className="mt-5 dark:text-custom-300 text-[18px]">
          Applications of API keys
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          {ApiKeyUses.map((thing, index) => (
            <ThingsCard data={thing} key={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default observer(ApiKeyList);

const ThingsCard = props => {
  const { title, icon, description, time, link } = props.data;
  return (
    <div className="dark:bg-neutral-150 rounded-lg p-4 flex flex-col bg-custom-950 justify-between">
      <div>
        {icon}
        <h1 className="dark:text-storm-dust-100 font-medium text-neutral-150 mt-3">
          {title}
        </h1>
        <p className="mt-2 dark:text-custom-300 text-custom-410 text-sm">
          {description}
        </p>
      </div>
      <div className="mt-6 flex flex-row justify-end">
        <LinkButton
          size="sm"
          href={link}
          color="default"
          className="group flex flex-row items-center gap-2"
        >
          Read more
          <RightPaginationIcon className="" />
        </LinkButton>
      </div>
    </div>
  );
};
